import React, { useState, useEffect, useCallback, useRef } from 'react';
import { observer } from 'mobx-react';
import { Box, Flex, Text } from 'core/components';
import { Collapse, Tag } from '@blueprintjs/core';
import styled from 'styled-components';
import { useHistory, useLocation } from 'react-router-dom';
import { FiChevronDown, FiChevronUp, FiChevronRight } from 'react-icons/fi';
import moment from 'moment';
import { DEFAULT_USER_NAME } from './utils';

const getItemBg = (isActive, theme) => {
  if (isActive) {
    return theme.name === 'light'
      ? theme.colors.lightGray4 || '#EBF1FA'
      : theme.colors.gray8 || 'rgba(255,255,255,0.05)';
  }
  return 'transparent';
};

const SidebarContainer = styled(Box)`
  width: 320px;
  min-width: 320px;
  border-left: ${({ theme }) => theme.borders.thinLight};
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => (theme.name === 'light' ? theme.colors.lightGray5 : 'transparent')};
  height: 100%;
`;

const VersionHistoryHeader = styled(Flex)`
  padding: 16px;
  border-bottom: ${({ theme }) => theme.borders.thinLight};
  align-items: center;
  justify-content: space-between;
`;

const HeaderCloseButton = styled.button`
  font: inherit;
  color: inherit;
  background: transparent;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  svg {
    stroke: currentColor !important;
    width: 20px;
    height: 20px;
  }

  &:hover {
    background-color: ${({ theme }) => (theme.name === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)')};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
  }
`;

const VersionList = styled(Box)`
  flex: 1;
  overflow-y: auto;
`;

const VersionItemButton = styled.button`
  font: inherit;
  color: inherit;
  width: 100%;
  text-align: left;
  border: none;
  border-bottom: ${({ theme }) => theme.borders.thinLight};
  padding: 16px;
  cursor: pointer;
  background-color: ${({ $isActive, theme }) => getItemBg($isActive, theme)};
  transition: background-color 0.15s ease-in-out;

  &:hover {
    background-color: ${({ $isActive, theme }) => {
      if ($isActive) {
        return getItemBg(true, theme);
      }
      return theme.name === 'light' ? theme.colors.lightGray4 : 'rgba(255,255,255,0.02)';
    }};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: -2px;
  }
`;

const CommentText = styled(Text)`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.muted};
  font-style: italic;
  margin-top: 4px;
  word-wrap: break-word;
  opacity: 0.85;
`;

const InstructionsHeaderButton = styled.button`
  font: inherit;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  cursor: pointer;
  border: none;
  border-top: ${({ theme }) => theme.borders.thinLight};
  background-color: ${({ theme }) =>
    theme.name === 'light' ? theme.colors.lightGray4 || '#EBF1FA' : 'rgba(255,255,255,0.05)'};
  font-weight: 600;
  color: ${({ theme }) => (theme.name === 'light' ? '#000000' : '#FFFFFF')};

  svg {
    stroke: currentColor !important;
    width: 16px;
    height: 16px;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: -2px;
  }
`;

const InstructionsContent = styled(Box)`
  padding: 16px;
  background-color: ${({ theme }) =>
    theme.name === 'light' ? theme.colors.lightGray4 || '#EBF1FA' : 'rgba(255,255,255,0.05)'};
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.muted};

  ul {
    margin-top: 8px;
    padding-left: 20px;
  }
`;

const formatDate = (cdate) => {
  if (!cdate) {
    return 'Legacy Version';
  }
  const m = moment(cdate);
  return m.isValid() ? m.format('MMM D, YYYY - h:mm A') : 'Legacy Version';
};

const getNavigationUrl = (matchUrl, versionId, isLatest) => {
  const path = isLatest ? 'latest' : `${versionId}/view`;
  return `${matchUrl}/${path}`;
};

const ContextSidebar = ({ collection, matchUrl = '', onClose }) => {
  const history = useHistory();
  const location = useLocation();
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const hasUserToggledRef = useRef(false);

  const versions = collection.models;

  useEffect(() => {
    if (!hasUserToggledRef.current) {
      setInstructionsOpen(versions.length === 0);
    }
  }, [versions.length]);

  const handleToggleInstructions = useCallback(() => {
    hasUserToggledRef.current = true;
    setInstructionsOpen((prev) => !prev);
  }, []);

  const handleVersionClick = useCallback(
    (versionId, isLatest) => {
      history.push(getNavigationUrl(matchUrl, versionId, isLatest));
    },
    [history, matchUrl]
  );

  const isRouteActive = useCallback(
    (versionId, isLatest) => {
      if (isLatest && location.pathname.endsWith('/latest')) {
        return true;
      }
      if (!isLatest) {
        return location.pathname.endsWith(`/${versionId}/view`);
      }
      return false;
    },
    [location.pathname]
  );

  return (
    <SidebarContainer>
      <VersionHistoryHeader>
        <Text fontSize="14px" fontWeight="600">
          Version History
        </Text>
        <HeaderCloseButton
          type="button"
          title="Hide Version History"
          aria-label="Hide Version History"
          onClick={(e) => {
            e.stopPropagation();
            if (onClose) {
              onClose();
            }
          }}
        >
          <FiChevronRight />
        </HeaderCloseButton>
      </VersionHistoryHeader>

      <VersionList>
        {versions.map((model, index) => {
          const { version } = model;
          const isLatest = index === 0;
          const isActive = isRouteActive(version, isLatest);
          const formattedDate = formatDate(model.get('cdate'));
          const user = model.get('user');
          const userName = user?.user_full_name || DEFAULT_USER_NAME;
          const comment = model.get('comment');

          return (
            <VersionItemButton key={version} $isActive={isActive} onClick={() => handleVersionClick(version, isLatest)}>
              <Flex justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Text fontWeight="600" color="body">
                  {formattedDate}
                </Text>
                {isLatest && (
                  <Box ml={2}>
                    <Tag intent="primary" minimal>
                      latest
                    </Tag>
                  </Box>
                )}
              </Flex>

              <Flex flexDirection="column">
                <Text as="div" fontSize="13px" color="body">
                  {userName}
                </Text>

                {comment && <CommentText as="div">{comment}</CommentText>}
              </Flex>
            </VersionItemButton>
          );
        })}
      </VersionList>

      <Box mt="auto">
        <InstructionsHeaderButton
          type="button"
          onClick={handleToggleInstructions}
          aria-expanded={instructionsOpen}
          aria-controls="instructions-guidelines-content"
        >
          <Text>Instructions & Guidelines</Text>
          {instructionsOpen ? <FiChevronDown /> : <FiChevronUp />}
        </InstructionsHeaderButton>

        <Collapse isOpen={instructionsOpen} id="instructions-guidelines-content">
          <InstructionsContent>
            Provide details about your network environment to help AI Advisor deliver more accurate and relevant
            insights. Include information such as:
            <ul>
              <li>Network design and architecture (campus, WAN, data center, cloud)</li>
              <li>Naming conventions for devices, interfaces, and sites</li>
              <li>IP addressing schema</li>
              <li>VLAN or VRF convention</li>
              <li>Device types and vendors</li>
              <li>Network segments and their purposes (e.g. Data Centers, WAN network, Campus network, etc.)</li>
              <li>Critical applications and traffic patterns</li>
              <li>Any specific operational procedures or constraints</li>
              <li>Limit to 100K characters</li>
            </ul>
          </InstructionsContent>
        </Collapse>
      </Box>
    </SidebarContainer>
  );
};

export default observer(ContextSidebar);
